import { useEffect, useMemo } from 'react';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { useThemeMediaQuery } from '@fuse/hooks';
import { useTranslation } from 'react-i18next';
import FuseLoading from '@fuse/core/FuseLoading';

import ru_RU from '@react-pdf-viewer/locales/lib/ru_RU.json';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const PdfRender = ({ pdfFile, isFetching }) => {
  const { t } = useTranslation('translations');
  const { content } = pdfFile;

  const base64ToBlob = (base64, type = 'application/pdf') => {
    const base64Data = base64.split(',')[1] || base64;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  };

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  const zoomPluginInstance = zoomPlugin();

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [defaultTabs[0]],
    toolbarPlugin: {
      getButtons: () => [],
    },
    renderToolbar: (Toolbar) => <Toolbar>{({ Slot }) => <>{}</>}</Toolbar>,
  });

  const { ZoomInButton, ZoomOutButton, zoomTo, CurrentScale } = zoomPluginInstance;

  const fileUrl = useMemo(() => {
    const base64 = pdfFile?.fileBase64 || content;
    if (!base64) return null;

    const blob = base64ToBlob(base64);
    const url = URL.createObjectURL(blob);
    return url;
  }, [pdfFile, content]);

  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const CustomZoomControls = () => {
    return (
      <div className="flex items-center gap-2">
        <CurrentScale>
          {(props) => (
            <span className="text-sm text-gray-600">
              {t('Scale')}: {Math.round(props.scale * 100)}%
            </span>
          )}
        </CurrentScale>
        {/* <select
          className="border rounded px-2 py-1 text-sm text-black"
          onChange={(e) => zoomTo(parseFloat(e.target.value))}
        >
          {isMobile && <option value={0.3}>30%</option>}
          <option value={0.5}>50%</option>
          <option value={0.75}>75%</option>
          <option value={1}>100%</option>
          <option value={1.25}>125%</option>
          <option value={1.5}>150%</option>
          <option value={2}>200%</option>
        </select> */}
        <div className='mx-4'></div>
        {!isMobile && (
          <>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-all shadow-sm"
                onClick={() => zoomTo(SpecialZoomLevel.ActualSize)}
              >
                {t('ActualSize')}
              </button>

              {/* <button
                className="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-all shadow-sm"
                onClick={() => zoomTo(SpecialZoomLevel.PageFit)}
              >
                {t('PageFit')}
              </button> */}

              <button
                className="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-all shadow-sm"
                onClick={() => zoomTo(SpecialZoomLevel.PageWidth)}
              >
                {t('PageWidth')}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 w-full h-full overflow-auto">
      {fileUrl && (
        <>
          <div className="flex items-center gap-2 justify-center py-2 bg-gray-100 border-b">
            <ZoomOutButton />
            <CustomZoomControls />
            <ZoomInButton />
          </div>

          <div
            className="flex-1 overflow-auto border border-gray-300"
            style={{ height: 'calc(100% - 50px)' }}
          >
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
              <Viewer
                fileUrl={fileUrl}
                plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
                localization={ru_RU}
              />
            </Worker>
          </div>
        </>
      )}

      {isFetching && (
        <div className="h-full flex items-center justify-center">
          <FuseLoading />
        </div>
      )}

      {!fileUrl && !isFetching && (
        <div className="h-full flex items-center justify-center">
          <p className="text-gray-500">{t('FileNotFound')}</p>
        </div>
      )}
    </div>
  );
};

export default PdfRender;
